import type { DestinationWithCount } from '@/services/ResearchService';
import type { ShortlistWithVenue } from './types';

/**
 * Expand regions into flat list of searchable destinations
 * Used for search operations that need to iterate over all cities
 */
export function expandDestinationsForSearch(
  destinations: DestinationWithCount[]
): Array<{ id: string; city: string; country: string; parentRegionId?: string }> {
  const searchable: Array<{ id: string; city: string; country: string; parentRegionId?: string }> = [];
  
  for (const dest of destinations) {
    if (dest.destination_type === 'region' && dest.children?.length) {
      // Expand region into its child cities
      for (const child of dest.children) {
        searchable.push({
          id: child.id,
          city: child.city,
          country: child.country,
          parentRegionId: dest.id,
        });
      }
    } else {
      // Standalone city/country
      searchable.push({
        id: dest.id,
        city: dest.city,
        country: dest.country,
      });
    }
  }
  
  return searchable;
}

/**
 * Recalculate shortlist counts from actual shortlist data
 * Aggregates children counts for regions
 * This ensures counts are always accurate and regions show aggregated totals
 */
export function recalculateShortlistCounts(
  destinations: DestinationWithCount[],
  shortlist: ShortlistWithVenue[]
): DestinationWithCount[] {
  // Count shortlist items per destination ID
  const counts = new Map<string, number>();
  for (const item of shortlist) {
    const current = counts.get(item.research_destination_id) || 0;
    counts.set(item.research_destination_id, current + 1);
  }
  
  // Update destinations with counts
  return destinations.map(dest => {
    // Update this destination's count
    let updated: DestinationWithCount = {
      ...dest,
      shortlist_count: counts.get(dest.id) || 0,
    };
    
    // If it's a region, update children and aggregate
    if (dest.destination_type === 'region' && dest.children) {
      const childrenUpdated = dest.children.map(child => ({
        ...child,
        shortlist_count: counts.get(child.id) || 0,
      }));
      
      updated = {
        ...updated,
        children: childrenUpdated,
        // Aggregate: region's own count + sum of children
        shortlist_count: (counts.get(dest.id) || 0) + childrenUpdated.reduce(
          (sum, c) => sum + c.shortlist_count, 
          0
        ),
      };
    }
    
    return updated;
  });
}

/**
 * Find destination by ID, searching both top-level and children
 */
export function findDestinationById(
  destinations: DestinationWithCount[],
  id: string
): DestinationWithCount | undefined {
  for (const dest of destinations) {
    if (dest.id === id) return dest;
    if (dest.children) {
      const child = dest.children.find(c => c.id === id);
      if (child) return child;
    }
  }
  return undefined;
}

/**
 * Find parent region for a child destination
 */
export function findParentRegion(
  destinations: DestinationWithCount[],
  childId: string
): DestinationWithCount | undefined {
  for (const dest of destinations) {
    if (dest.destination_type === 'region' && dest.children) {
      const child = dest.children.find(c => c.id === childId);
      if (child) return dest;
    }
  }
  return undefined;
}
